import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { PencilIcon, Undo2Icon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon, type IconData } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import { SIDEBAR_ICON_NAMES, type SidebarIconName } from "../SidebarIcons";
import { SIDEBAR_ICONS } from "../SidebarIcons.constants";

const IconChooserGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  max-width: 220px;
`;

const IconChooserButton = styled.button`
  cursor: pointer;
  border: none;
  background: transparent;
  border-radius: 5px;
  padding: 7.5px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(.is-selected) {
    background-color: #f6f6f6;
  }

  &.is-selected {
    background-color: #dfdfdf;
  }

  > svg {
    margin: 0;
  }
`;

const TriggerIconWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EditBadge = styled.span`
  position: absolute;
  right: 5px;
  bottom: -2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 1px #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  > svg {
    width: 10px;
    height: 10px;
    margin: 0;
  }
`;

type SidebarIconChooserProps = {
  icon?: IconData;
  selected?: string | null;
  onChange: (icon: SidebarIconName | null) => void;
};

export const SidebarIconChooser = ({
  icon,
  selected,
  onChange,
}: SidebarIconChooserProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selectIcon = useCallback(
    (name: SidebarIconName | null) => {
      onChange(name);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <TriggerIconWrap>
      <Popup
        on="click"
        position="bottom left"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        trigger={
          <Button size="small" variant="ghost" title={t("generic.chooseIcon")}>
            <Icon icon={icon} />
          </Button>
        }
      >
        <IconChooserGrid>
          {SIDEBAR_ICON_NAMES.map((iconName) => (
            <IconChooserButton
              key={iconName}
              type="button"
              className={cx(selected === iconName && "is-selected")}
              aria-label={iconName}
              onClick={() => selectIcon(iconName)}
            >
              <Icon icon={SIDEBAR_ICONS[iconName]} size="large" />
            </IconChooserButton>
          ))}
        </IconChooserGrid>
        {selected ? (
          <Button
            size="small"
            variant="outline"
            onClick={() => selectIcon(null)}
            style={{ marginTop: 8 }}
            fluid
          >
            <Icon icon={Undo2Icon} />
            {t("generic.resetIcon")}
          </Button>
        ) : null}
      </Popup>
      <EditBadge aria-hidden>
        <Icon icon={PencilIcon} size="small" />
      </EditBadge>
    </TriggerIconWrap>
  );
};
